import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, Observable, skipWhile, take, withLatestFrom } from 'rxjs';
import { AccessRequestsFacade } from '../access-requests';
import { InvitationFacade } from '../invitation/store';
import {
  AccessibleTenant,
  AllocateEntitlementPayload,
  ApplicationCard,
  ApplicationCardButtonAction,
  AppState,
  ArchiveTenantPayload,
  CreateChildTenant,
  CreateTenant,
  Entitlement,
  EntitlementData,
  FavoriteApps,
  ICatalogInfo,
  LeaveTenantPayload,
  ResourceRoleAction,
  TenantHierarchyUsersRequest,
  UpdateTenant,
  UploadLogoPreOperation,
  UserFeedback,
  UserRole,
} from '../lemans-app.model';
import {
  applyCustomProvisioningEntitlement,
  applyRegularEntitlement,
  approveAccess,
  archiveTenant,
  cardClicked,
  changeParentOrganization,
  checkUserCanChangeTenantHierarchy,
  clearRefreshTenantListGrid,
  convertToParentOrganization,
  convertToSubOrganization,
  createChildTenant,
  createSelectedFakeEntitlements,
  createTenant,
  deallocateEntitlement,
  generateInviteCode,
  getAllTenantResourceRoles,
  getSuggestedTenantByCode,
  getTenantDetails,
  getTenantEffectiveRoles,
  getTenantUsers,
  getTenantUtilityTokens,
  getUserMembership,
  getUserPreferences,
  getUserPreferencesFailed,
  getUserResources,
  getUserRolesForTenant,
  initializeCustomProvisioning,
  leaveCustomProvisioning,
  leaveTenant,
  noLastAccessedTenant,
  removeAccess,
  resetAllocateEntitlementResponseData,
  setEditUserDetails,
  setFavoriteApps,
  setTenantCreated,
  setUserId,
  shareFeedback,
  switchTenant,
  transferOwnership,
  unarchiveTenant,
  updateAccess,
  updatePreferredLanguage,
  updateTenant,
  updateUserDescriptionForAllocationId,
  updateUserDescriptionOnFailure,
  updateUserDescriptionOnSuccess,
  uploadOrgLogo,
} from './user-data.action';
import {
  accessibleTenants,
  currentTenant,
  currentTenantContext,
  currentTenantId,
  effectiveRoles,
  hasAnyServiceRole,
  hasBusinessPartnerId,
  manageableResources,
  newTenantCreated,
  noSuggestedTenant,
  preferredLanguage,
  selectAllocateEntitlementResponse,
  selectCanChangeTenantHierarchy,
  selectCustomProvisioningState,
  selectEditUser,
  selectExitFromProvisioning,
  selectFavoriteApps,
  selectManageableResourceIds,
  selectPendingServices,
  selectPreferences,
  selectRefreshOrgEntitlements,
  selectRefreshRolesGrid,
  selectRefreshTenantListGrid,
  selectRefreshUserEntitlements,
  selectResourceRoles,
  selectResources,
  selectServices,
  selectTenantUsers,
  selectTenantUtilityTokenBalance,
  selectTenantUtilityTokenEntitlements,
  selectUnallocatedEntitlementsCount,
  selectUserData,
  selectUserID,
  selectUserMembership,
  selectUserName,
  selectUserResourceRoles,
  suggestedTenant,
  tenantResourceHierarchy,
  userPermissionsEvalResult,
} from './user-data.selectors';
import {
  ChangeParentPayload,
  ConvertToParentOrganization,
  ConvertToSubOrganization,
  DeallocateEntitlementRequestBody,
  TransferOwnership,
} from './user.model';

@Injectable({
  providedIn: 'root',
})
export class UserDataFacade {
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);

  userData$ = this.store$.select(selectUserData);
  hasBusinessPartnerId$ = this.store$.select(hasBusinessPartnerId);
  userID$ = this.store$.select(selectUserID).pipe(filter((value) => value !== undefined));
  userName$ = this.store$.select(selectUserName);
  suggestedTenant$ = this.store$.select(suggestedTenant).pipe(filter((value) => value !== undefined));
  noSuggestedTenant$ = this.store$.select(noSuggestedTenant);
  currentTenantId$ = this.store$.select(currentTenantId).pipe(filter((tenantId) => tenantId !== undefined));
  newTenantCreated$ = this.store$
    .select(newTenantCreated)
    .pipe(filter((newTenantCreated) => newTenantCreated === true));
  currentTenantContext$ = this.store$.pipe(currentTenantContext);
  selectedTenant$ = this.currentTenantContext$.pipe(
    filter((tenantContext) => tenantContext !== 'NotSelected' && tenantContext !== 'Personal'),
  );
  hasPersonalTenantContext$ = this.currentTenantContext$.pipe(map((tenantContext) => tenantContext === 'Personal'));
  noLastAccessedTenant$ = this.currentTenantContext$.pipe(map((tenantContext) => tenantContext === 'NotSelected'));
  // from https://ngrx.io/guide/store/selectors#solution-extracting-a-pipeable-operator
  manageableResources$ = this.store$.pipe(manageableResources);
  userPermissionsEvalResult$ = this.store$.pipe(userPermissionsEvalResult);
  currentTenant$ = this.store$.select(currentTenant).pipe(filter((currentTenant) => currentTenant !== undefined));
  isSubOrg$ = this.currentTenant$.pipe(map((tenant) => Boolean(tenant?.parent)));
  allAccessibleTenants$ = this.store$.select(accessibleTenants).pipe(filter((tenants) => tenants !== undefined));
  accessibleTenants$ = this.allAccessibleTenants$.pipe(
    withLatestFrom(this.userID$),
    map(([tenants, userID]) => tenants?.filter((tenant) => tenant.id !== userID)),
  );
  effectiveRoles$ = this.store$.select(effectiveRoles).pipe(filter((effectiveRoles) => effectiveRoles !== undefined));
  resourceRoles$ = this.store$.select(selectResourceRoles);
  userResourceRoles$ = this.store$
    .select(selectUserResourceRoles)
    .pipe(filter((userResourceRoles) => userResourceRoles !== undefined));
  allResourceRoles$ = this.resourceRoles$.pipe(filter((allResourceRoles) => allResourceRoles !== undefined));
  getResources$ = this.store$.select(selectResources);
  tenantUsers$ = this.store$.select(selectTenantUsers);
  getSelectedEditUser$ = this.store$.select(selectEditUser);
  services$ = this.store$.select(selectServices);
  tokenEntitlements$ = this.store$
    .select(selectTenantUtilityTokenEntitlements)
    .pipe(skipWhile((tokenEntitlements) => tokenEntitlements === undefined));
  tokenBalance$ = this.store$.select(selectTenantUtilityTokenBalance);
  pendingServices$ = this.store$.select(selectPendingServices);
  currentCustomProvisioningData$ = this.store$.select(selectCustomProvisioningState);
  currentPreferences$ = this.store$.select(selectPreferences);
  exitFromProvisioning$ = this.store$.select(selectExitFromProvisioning);
  favoriteApps$ = this.store$.select(selectFavoriteApps);
  preferredLanguage$ = this.store$.select(preferredLanguage).pipe(filter((lang) => lang !== undefined));
  // for transition - there's no good reason for this to be here
  // right now doing this to keep it easy to prototype
  invitations = new InvitationFacade(this.store$);
  accessRequests = new AccessRequestsFacade(this.store$);

  refreshUserEntitlementsGrid$ = this.store$.select(selectRefreshUserEntitlements);
  refreshOrgEntitlementsGrid$ = this.store$.select(selectRefreshOrgEntitlements);
  refreshUserRolesGrid$ = this.store$.select(selectRefreshRolesGrid);
  refreshTenantListGrid$ = this.store$.select(selectRefreshTenantListGrid);

  unallocatedEntitlementsCount$ = this.store$.select(selectUnallocatedEntitlementsCount);

  entitlementAllocationResponseData$ = this.store$
    .select(selectAllocateEntitlementResponse)
    .pipe(filter((allocateResponse) => allocateResponse !== undefined));

  canChangeTenantHierarchy$ = this.store$.select(selectCanChangeTenantHierarchy);
  userMembership$ = this.store$.select(selectUserMembership).pipe(filter((membership) => membership != null));
  hasAnyServiceRole$ = this.store$.pipe(hasAnyServiceRole);
  manageableResourceIds$ = this.store$.pipe(selectManageableResourceIds);
  tenantResourceHierarchy$ = this.store$.pipe(tenantResourceHierarchy);

  setUserID(userID: string): void {
    this.store$.dispatch(setUserId({ payload: userID.replaceAll('-', '') }));
  }

  getUserPreferences(): void {
    this.store$.dispatch(getUserPreferences());
  }

  getAllTenantResourceRoles(tenantId: string): void {
    this.store$.dispatch(getAllTenantResourceRoles({ payload: tenantId }));
  }

  getUserRolesForTenant(tenantId: string, userId: string): void {
    this.store$.dispatch(getUserRolesForTenant({ tenantId: tenantId, userId: userId }));
  }

  languageChange(languageCode: string): void {
    this.store$.dispatch(updatePreferredLanguage({ preferredLanguage: languageCode }));
  }

  tenantChange(tenantId: string, navigateTo?: string): void {
    this.store$.dispatch(switchTenant({ value: tenantId, navigateTo: navigateTo }));
  }

  createTenant(tenant: CreateTenant, blobImage: Blob, idempotencyKey: string): void {
    this.store$.dispatch(
      createTenant({
        blobImage: blobImage,
        payload: tenant,
        idempotencyKey,
      }),
    );
  }

  createChildTenant(
    childTenant: CreateChildTenant,
    blobImage: Blob,
    idempotencyKey: string,
    parentTenantName: string,
  ): void {
    this.store$.dispatch(
      createChildTenant({
        blobImage: blobImage,
        payload: childTenant,
        idempotencyKey,
        parentTenantName,
      }),
    );
  }

  resetTenantCreated(): void {
    this.store$.dispatch(setTenantCreated({ value: false }));
  }

  updateTenant(tenant: UpdateTenant, blobImage?: Blob): void {
    this.store$.dispatch(updateTenant({ blobImage: blobImage, payload: tenant }));
  }

  uploadOrgLogo(tenantId: string, blobImage: Blob, preOperation: UploadLogoPreOperation): void {
    this.store$.dispatch(uploadOrgLogo({ blobImage: blobImage, preOperation: preOperation, tenantId: tenantId }));
  }

  generateInviteCode(tenantId: string): void {
    this.store$.dispatch(generateInviteCode({ tenantId: tenantId }));
  }

  shareFeedback(feedback: UserFeedback): void {
    this.store$.dispatch(shareFeedback({ payload: feedback }));
  }

  getUserResources(tenantId: string): void {
    this.store$.dispatch(getUserResources({ value: tenantId }));
  }
  getSuggestedTenantByCode(inviteCode: string): void {
    this.store$.dispatch(getSuggestedTenantByCode({ inviteCode: inviteCode }));
  }

  createSelectedFakeEntitlements(
    codes: ICatalogInfo[],
    quantity: number = 1,
    duration: number = 365,
    effectiveDate: string,
  ): void {
    this.store$.dispatch(
      createSelectedFakeEntitlements({
        codes: codes,
        quantity: quantity,
        duration: duration,
        effectiveDate: effectiveDate,
      }),
    );
  }

  applyCustomProvisioningEntitlement(
    userId: string,
    tenantId: string,
    entitlementData: EntitlementData,
    payload: AllocateEntitlementPayload,
    domain: string,
  ): void {
    const params = {
      userId: userId,
      tenantId: tenantId,
      entitlementData: entitlementData,
      payload,
      domain: domain,
    };
    this.store$.dispatch(applyCustomProvisioningEntitlement(params));
  }

  applyRegularEntitlement(
    userId: string,
    tenantId: string,
    entitlementData: EntitlementData,
    payload: AllocateEntitlementPayload,
    domain: string,
    organizationName?: string | undefined,
  ): void {
    const regularParams = {
      userId: userId,
      tenantId: tenantId,
      entitlementData: entitlementData,
      payload,
      domain: domain,
      organizationName: organizationName,
    };
    this.store$.dispatch(applyRegularEntitlement(regularParams));
  }

  deallocateEntitlement(
    tenantId: string,
    entitlement: Entitlement,
    requestBody: DeallocateEntitlementRequestBody,
    organizationName?: string,
  ): void {
    const payload = {
      tenantId: tenantId,
      entitlement: entitlement,
      organizationName: organizationName,
      requestBody,
    };
    this.store$.dispatch(deallocateEntitlement(payload));
  }

  updateAccess(payload: ResourceRoleAction[], tenantId: string, flow?: 'add' | 'edit' | 'approve'): void {
    this.store$.dispatch(updateAccess({ flow: flow, payload: payload, tenantId: tenantId }));
  }
  approveAccess(payload: ResourceRoleAction, userData: any): void {
    this.store$.dispatch(approveAccess({ payload, userData }));
  }
  setEditUser(payload: Partial<UserRole>): void {
    this.store$.dispatch(setEditUserDetails({ payload }));
  }

  removeAccess(payload: ResourceRoleAction): void {
    this.store$.dispatch(removeAccess({ payload: payload }));
  }

  getUserPreferencesFailed(httpError: any): void {
    this.store$.dispatch(getUserPreferencesFailed({ value: httpError }));
  }

  noLastAccessedTenant(): void {
    this.store$.dispatch(noLastAccessedTenant());
  }

  getTenantUtilityTokens(tenantId: string): void {
    this.store$.dispatch(getTenantUtilityTokens({ value: tenantId }));
  }
  initializeCustomProvisioning(customProvisioningData: EntitlementData): void {
    this.store$.dispatch(
      initializeCustomProvisioning({
        payload: {
          app: customProvisioningData.app,
          continue: customProvisioningData?.continue,
          tenantId: customProvisioningData.tenantId,
          entitlement: customProvisioningData.entitlement,
          serviceId: customProvisioningData.serviceId,
          userId: customProvisioningData.userId,
        },
      }),
    );
  }

  resetCustomProvisioningData(): void {
    this.store$.dispatch(leaveCustomProvisioning());
  }

  setFavoriteApps(favoriteApps: FavoriteApps): void {
    this.store$.dispatch(setFavoriteApps({ favoriteApps }));
  }

  getTenantUsers(payload: TenantHierarchyUsersRequest): void {
    this.store$.dispatch(getTenantUsers({ payload }));
  }

  cardClicked(action: ApplicationCardButtonAction, card: ApplicationCard): void {
    this.store$.dispatch(cardClicked({ action: action, card: card }));
  }

  archiveTenant(archiveTenantPayload: ArchiveTenantPayload): void {
    this.store$.dispatch(archiveTenant({ archiveTenantPayload }));
  }

  unarchiveTenant(tenant: AccessibleTenant): void {
    this.store$.dispatch(unarchiveTenant({ tenant }));
  }

  resetAllocateEntitlementResponseData(): void {
    this.store$.dispatch(resetAllocateEntitlementResponseData());
  }

  clearRefreshTenantListGrid(): void {
    this.store$.dispatch(clearRefreshTenantListGrid());
  }

  leaveTenant(leaveTenantPayload: LeaveTenantPayload): void {
    this.store$.dispatch(
      leaveTenant({
        payload: leaveTenantPayload,
      }),
    );
  }

  getTenantDetails(tenantId: string): void {
    this.store$.dispatch(getTenantDetails({ value: tenantId }));
  }

  transferOwnership(payload: TransferOwnership): void {
    this.store$.dispatch(transferOwnership({ payload }));
  }
  changeParentOrganization(payload: ChangeParentPayload, idempotencyKey: string): void {
    this.store$.dispatch(changeParentOrganization({ payload, idempotencyKey }));
  }

  checkUserCanChangeTenantHierarchy(tenantId: string, userId: string): void {
    this.store$.dispatch(checkUserCanChangeTenantHierarchy({ tenantId, userId }));
  }

  convertToSubOrganization(payload: ConvertToSubOrganization): void {
    this.store$.dispatch(convertToSubOrganization({ payload }));
  }

  convertToParentOrganization(payload: ConvertToParentOrganization): void {
    this.store$.dispatch(convertToParentOrganization({ payload }));
  }

  getUserMembership(tenantId: string, userId: string): void {
    this.store$.dispatch(getUserMembership({ tenantId, userId }));
  }

  getTenantEffectiveRoles(tenantId: string, userId: string): void {
    this.store$.dispatch(getTenantEffectiveRoles({ tenantId, userId }));
  }

  updateUserDescriptionForAllocationId$(payload: {
    allocationId: string;
    entitlementId: string;
    userDescription: string;
    tenantId: string;
  }): Observable<boolean> {
    this.store$.dispatch(updateUserDescriptionForAllocationId(payload));
    // We return an Observable<boolean> (instead of void) so the caller (the editable user description cell renderer) knows whether the update actually succeeded or failed.
    // This allows the renderer to reset its input back to the last known-good value when the backend update fails, rather than leaving the rejected, unsaved edit visible in the UI.
    // Correlate this specific request with its async response by listening on the actions stream for
    // the success/failure action matching this allocationId + entitlementId, resolving with a single boolean
    // once a match is found (take(1) completes the stream after the first matching emission).
    return this.actions$.pipe(
      ofType(updateUserDescriptionOnSuccess, updateUserDescriptionOnFailure),
      filter(
        (action) => action.allocationId === payload.allocationId && action.entitlementId === payload.entitlementId,
      ),
      take(1),
      map((action) => action.type === updateUserDescriptionOnSuccess.type),
    );
  }
}
